import React, { Fragment } from "react";
import { useSelector } from "react-redux";
import { motion} from "framer-motion";
import { APP_HEADER_HEIGHT } from "../settings";

import "../styles/page.css";

/*provides support for auto computation of page height as well as animation when loading the page*/
const Root = props => 
{
    const {id, className, animation, children} = props;

    if (animation)
        return (
            <motion.div id={id}
                className={`page ${className ? className : null}`}           
                initial={animation.initial || undefined}
                animate={animation.animate || undefined}
                exit={animation.exit || undefined}
                transition={animation.transition || undefined}
            >                
                { children}               
            </motion.div>
        );
    else
        return (
            <div id={id} className={`page ${className ? className : null}`}>{children}</div>
        );            
};

/*
page.css exists containing the non-theme css props
<div .page .{className}> -->.page bgColor=componentBg height: 100% + .css: (w:100%, flex, nowrap, overflow-x: auto and overflow-y: hidden so it only scrolls horizontally)
   <div .header> -->if icon or title or headerRenderer
      <icon .k-icon> --> if icon --> color: primary + .css (size: 26px, marginRight:8)
      <div .title> --> if title --> color: primary +.css (18px, Italic, 500)
   </div>
   <div .content> --> page content height is computed = 100% - 56px where 56 is the header height 
   </div>
</div>
 
you can pass id as a page id which is unique across the app; it's optional
to disable animation, don't pass props.animation
className is assigned to the root div
icon, if passed, does not include k-i-
you can pass headerRenderer instead of icon and title
*/
export const Page = props => {
    const {
        id, animation, className, children,
        icon, title, headerRenderer,
        ...other} = props; 

    const currentTheme = useSelector(state => state.currentTheme);
    const pageContentHeight = "100%";//if we want to re-include the header => "calc(100% - 36px)" 36 is height of page header set in page.css 

    return (
        <Root id={id} animation={animation} className={className}>
            {
                (headerRenderer || icon || title) && currentTheme.id < 1 &&
                <div className="header">
                {
                    headerRenderer ? headerRenderer(props)
                        : (
                            icon ? 
                                (
                                title ? <Fragment>
                                            <div className={`k-icon k-i-${icon}`}></div> 
                                            <span className="title">{title}</span> 
                                        </Fragment>
                                        : <div className={`k-icon k-i-${icon}`}></div>
                                )
                                :
                                (title ? <span className="title">{title}</span> : null)
                        )
                }
                </div>
            }
            <div className="content">
                { React.Children.toArray(children)[0] }
            </div>            
            { React.Children.count(children) > 1 && React.Children.toArray(props.children).slice(1) }
            <style>
                {
                    `
                        .page
                        {
                            background-color: ${currentTheme.componentBg};
                        }

                        .page > .header
                        {
                            padding-left: 8px;
                            background-color: ${currentTheme.headerBg};
                            border-bottom: 1px solid ${currentTheme.componentBorderColor};
                            border-left: 1px solid ${currentTheme.componentBorderColor};
                        }

                        .page > .header .k-icon
                        {
                            color: ${currentTheme.primary};
                        }

                        .page > .header .title
                        {
                            color: ${currentTheme.headerText};
                        }

                        .page > .content
                        {
                            height: ${pageContentHeight};
                            /*border-bottom: 1px solid ${currentTheme.componentBorderColor};*/
                        }    
                        
                        .page > .content .page-loader
                        {
                            position: absolute;
                            z-index: 999999999;
                            top: calc(50% - ${APP_HEADER_HEIGHT}px); 
                            left: calc(50% - 60px);
                            display: flex;
                            flex-direction: column;
                            justify-content: center;
                            align-items: center;
                            padding: 20px;
                            background-color: ${currentTheme.componentBg};
                            border: 1px solid ${currentTheme.componentBorderColor};
                            border-radius: 6px;
                        }

                        @media (max-width: 1024px)
                        {
                            .page > .content .page-loader
                            {
                                position: fixed;
                                top: 50%;
                                left: 50%;
                                z-index: 10030;
                                width: max-content;
                                max-width: calc(100vw - 32px);
                                padding: 16px;
                                box-sizing: border-box;
                                text-align: center;
                                transform: translate(-50%, -50%);
                            }
                        }
                    `
                }
            </style>   
        </Root>
    );   
}
